import { createBrowserRouter, redirect } from "react-router-dom";
import { NotFoundPage } from "@/pages/not-found-page";
// Layouts
import DashboardLayout from "@/components/layouts/dashboard-layout";
// Auth
import LoginPage from "@/pages/auth/login-page";
import ForgotPasswordPage from "@/pages/auth/forgot-password";
import { ProtectedRoute } from "@/features/auth/components/protected-route";
import { PublicRoute } from "@/features/auth/components/public-route";
import { RootLayout } from "@/components/layouts/root-layout";
// Stats
import StatisticsPage from "@/pages/dashboard/stats/statistics-page";

// Dilemmas
import DilemmasListPage from "@/pages/dashboard/dilemmas/dilemmas-list-page";
import DilemmaEditPage from "@/pages/dashboard/dilemmas/dilemma-edit-page";
import DilemmaCreatePage from "@/pages/dashboard/dilemmas/dilemma-create-page";
import DilemmaTranslatePage from "@/pages/dashboard/dilemmas/dilemma-translate-page";
import SubmissionsListPage from "@/pages/dashboard/dilemmas/submissions-list-page";
// Themes
import ThemesListPage from "@/pages/dashboard/themes/themes-list-page";
import ThemesCreatePage from "@/pages/dashboard/themes/themes-create-page";
import ThemesEditPage from "@/pages/dashboard/themes/themes-edit-page";
// Users
import UsersListPage from "@/pages/dashboard/users/users-list-page";
import UsersCreatePage from "@/pages/dashboard/users/users-create-page";
import UsersEditPage from "@/pages/dashboard/users/users-edit-page";
// History
import HistoryPage from "@/pages/dashboard/history/history-page";
// About
import AboutListPage from "@/pages/dashboard/about/about-list-page";
import AboutCreatePage from "@/pages/dashboard/about/about-create-page";
import AboutEditPage from "@/pages/dashboard/about/about-edit-page";
// Documentation
import DocumentationPage from "@/pages/dashboard/documentation/documentation-page";
// Warnings / Alerts
import WarningsPage from "@/pages/dashboard/warnings/warnings-page";
import AlertCreatePage from "@/pages/dashboard/warnings/alert-create-page";
import AlertEditPage from "@/pages/dashboard/warnings/alert-edit-page";
// Account
import AccountPage from "@/pages/dashboard/account/account-page";
// Settings
import SettingsPage from "@/pages/settings/settings-page";

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        loader: () => redirect("/login"),
      },
      {
        path: "/login",
        handle: { title: "titles.login" },
        element: (
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        ),
      },
      {
        path: "/forgot-password",
        handle: { title: "titles.forgot_password" },
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            Component: StatisticsPage,
            handle: { title: "titles.dashboard.stats" },
          },
          {
            path: "dilemmas",
            handle: { title: "titles.dashboard.dilemmas.list" },
            children: [
              {
                index: true,
                Component: DilemmasListPage,
              },
              {
                path: "create",
                Component: DilemmaCreatePage,
                handle: { title: "titles.dashboard.dilemmas.create" },
              },
              {
                path: ":id",
                Component: DilemmaEditPage,
                handle: { title: "titles.dashboard.dilemmas.edit" },
              },
              {
                path: ":id/translate",
                Component: DilemmaTranslatePage,
                handle: { title: "titles.dashboard.dilemmas.translate" },
              },
            ],
          },
          {
            path: "themes",
            handle: { title: "navigation_links.themes" },
            children: [
              {
                index: true,
                Component: ThemesListPage,
              },
              {
                path: "create",
                Component: ThemesCreatePage,
                handle: { title: "titles.dashboard.themes.create" },
              },
              {
                path: ":id",
                Component: ThemesEditPage,
                handle: { title: "titles.dashboard.themes.edit" },
              },
            ],
          },
          {
            path: "users",
            handle: { title: "navigation_links.users" },
            children: [
              {
                index: true,
                Component: UsersListPage,
              },
              {
                path: "create",
                Component: UsersCreatePage,
                handle: { title: "titles.dashboard.users.create" },
              },
              {
                path: ":id",
                Component: UsersEditPage,
                handle: { title: "titles.dashboard.users.edit" },
              },
            ],
          },
          {
            path: "documentation",
            handle: { title: "titles.dashboard.documentation.list" },
            Component: DocumentationPage,
          },
          {
            path: "warnings",
            handle: { title: "titles.dashboard.alerts" },
            children: [
              { index: true, Component: WarningsPage },
              {
                path: "create",
                Component: AlertCreatePage,
                handle: { title: "titles.dashboard.alert_create" },
              },
              {
                path: ":id",
                Component: AlertEditPage,
                handle: { title: "titles.dashboard.alert_edit" },
              },
            ],
          },
          {
            path: "history",
            Component: HistoryPage,
            handle: { title: "titles.dashboard.history" },
          },
          {
            path: "about",
            handle: { title: "navigation_links.informations" },
            children: [
              {
                index: true,
                Component: AboutListPage,
              },
              {
                path: "create",
                Component: AboutCreatePage,
                handle: { title: "titles.dashboard.about.create" },
              },
              {
                path: ":id",
                Component: AboutEditPage,
                handle: { title: "titles.dashboard.about.edit" },
              },
            ],
          },
          {
            path: "account",
            Component: AccountPage,
            handle: { title: "titles.dashboard.account" },
          },
          {
            path: "settings",
            Component: SettingsPage,
            handle: { title: "titles.dashboard.settings" },
          },
          {
            path: "submissions",
            handle: { title: "titles.dashboard.submissions" },
            children: [
              {
                index: true,
                Component: SubmissionsListPage,
              },
              {
                path: ":id",
                Component: DilemmaEditPage,
                handle: { title: "titles.dashboard.dilemmas.edit" },
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
        handle: { title: "titles.not_found" },
      },
    ],
  },
]);
